import { useEffect, useRef } from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import type { Project } from './api/projects'

type ProjectWorkspaceState = {
  activeProjectId: string
  setActiveProjectId: (projectId: string) => void
}

export const useProjectWorkspace = create<ProjectWorkspaceState>()(
  persist(
    (set) => ({
      activeProjectId: '',
      setActiveProjectId: (activeProjectId) => set({ activeProjectId }),
    }),
    { name: 'seolhwa.project-workspace' },
  ),
)

/**
 * Resolves one shared project across the dashboard and every workflow page.
 * A valid URL project takes precedence, then the remembered project, then the
 * first accessible project.
 */
export function useProjectSelection(
  projects: Project[] | undefined,
  preferredProjectId?: string,
) {
  const activeProjectId = useProjectWorkspace((state) => state.activeProjectId)
  const setActiveProjectId = useProjectWorkspace((state) => state.setActiveProjectId)
  const consumedPreferred = useRef<string>()
  const validPreferred = preferredProjectId !== consumedPreferred.current &&
    projects?.some((project) => project.id === preferredProjectId)
    ? preferredProjectId
    : undefined
  const validStored = projects?.some((project) => project.id === activeProjectId)
    ? activeProjectId
    : undefined
  const projectId = validPreferred ?? validStored ?? projects?.[0]?.id ?? ''

  useEffect(() => {
    if (validPreferred) consumedPreferred.current = validPreferred
    if (projectId && projectId !== activeProjectId) setActiveProjectId(projectId)
  }, [activeProjectId, projectId, setActiveProjectId, validPreferred])

  return {
    projectId,
    project: projects?.find((project) => project.id === projectId),
    setProjectId: setActiveProjectId,
  }
}
